import {
  Button,
  Stack,
  Group,
  Title,
  Text,
  PinInput,
  Space,
} from '@mantine/core';
import { LoginWrapper } from '../LoginWrapper';
import { useEffect, useMemo, useState } from 'react';
import { PageRoutes } from '../../constants/routes';
import { useNavigate } from 'react-router-dom';
import { MobileLogo } from './SignUpEmail';
import { primaryColor } from '../../constants/theme';
import { useMutation } from '@tanstack/react-query';
import { verifyOtp, resendVerificationEmail } from '../../services/api';
import { useApp } from '../../context/AppContext';

export const VerifyEmail = () => {
  const [oneTimeCode, setOneTimeCode] = useState('');
  const [isWrong, setIsWrong] = useState(false);
  const [badInput, setBadInput] = useState(false);
  const navigate = useNavigate();
  const { token } = useApp();

  useEffect(() => {
    if (isWrong) {
      setIsWrong(false);
    }
    if (badInput) {
      setBadInput(false);
    }
    reset();
  }, [oneTimeCode]);

  const { mutate, isPending, isError, reset } = useMutation({
    mutationFn: () => verifyOtp({ otp: oneTimeCode, token }),
    onSuccess: (isVerified: boolean) => {
      if (isVerified) {
        navigate(PageRoutes.ONBOARDING_BEST_DESCRIBES);
        return;
      } else {
        setIsWrong(true);
      }
    },
  });

  const {
    mutate: resendEmail,
    isPending: isResending,
    isError: resendError,
  } = useMutation({
    mutationFn: () => resendVerificationEmail(token),
  });

  const errorMessage = useMemo(() => {
    if (badInput) {
      return VerifyEmailErrorMessages.BAD_INPUT;
    }
    if (isError) {
      return VerifyEmailErrorMessages.VERIFY_OTP;
    }
    if (isWrong) {
      return VerifyEmailErrorMessages.IS_WRONG;
    }
    return undefined;
  }, [isError, isWrong, badInput]);

  const onSubmit = () => {
    const otp = oneTimeCode.split('');
    if (otp.length !== 6 || otp.some((char) => isNaN(Number(char)))) {
      setBadInput(true);
      return;
    }
    mutate();
  };

  return (
    <LoginWrapper animate={false}>
      <MobileLogo />
      <Stack align="center">
        <Title order={2} data-testid={VerifyEmailTestIds.TITLE}>
          Verify Your Email
        </Title>
        <Text
          size="md"
          maw={'450px'}
          data-testid={VerifyEmailTestIds.SUB_TITLE}
        >
          Please enter the verification code we have sent to your email address
        </Text>
        <PinInput
          error={isWrong || badInput}
          value={oneTimeCode}
          onChange={(value) => setOneTimeCode(value)}
          oneTimeCode
          type={'number'}
          length={6}
          aria-label="One time code"
          data-testid={VerifyEmailTestIds.PIN_INPUT}
        ></PinInput>
        {errorMessage && <VerifyEmailAlert message={errorMessage} />}
        <Button
          data-testid={VerifyEmailTestIds.VERIFY_BUTTON}
          fullWidth
          radius={'lg'}
          size={'md'}
          onClick={onSubmit}
          loading={isPending}
          color={primaryColor}
        >
          VERIFY CODE
        </Button>
        <Space h="1px"></Space>
        <Group gap={5} justify="center">
          <Text data-testid={VerifyEmailTestIds.DIDNT_RECEIVE}>
            Didn't recieve a code?
          </Text>
          <Button
            data-testid={VerifyEmailTestIds.RESEND_BUTTON}
            color={primaryColor}
            variant="white"
            fw={'bold'}
            p={0}
            onClick={() => resendEmail()}
            loading={isResending}
          >
            RESEND
          </Button>
          {resendError && (
            <VerifyEmailAlert message={VerifyEmailErrorMessages.RESEND_EMAIL} />
          )}
        </Group>
      </Stack>
    </LoginWrapper>
  );
};

const VerifyEmailAlert = ({ message }: { message: string }) => {
  return (
    <Text c="red" data-testid={VerifyEmailTestIds.ERROR_MESSAGE}>
      {message}
    </Text>
  );
};

export const VerifyEmailErrorMessages = {
  VERIFY_OTP: 'There was an error verifying your code. Please try again.',
  IS_WRONG: 'The code you entered is incorrect. Please try again.',
  BAD_INPUT: 'You must enter a 6 digit code.',
  RESEND_EMAIL:
    'There was an error resending the verification email. Please try again.',
};

export enum VerifyEmailTestIds {
  TITLE = 'verify-email-title',
  SUB_TITLE = 'verify-email-subtitle',
  DIDNT_RECEIVE = 'verify-email-didnt-receive',
  ERROR_MESSAGE = 'verify-email-error-message',
  SUCCESS_MESSAGE = 'verify-email-success-message',
  RESEND_BUTTON = 'verify-email-resend-button',
  VERIFY_BUTTON = 'verify-email-verify-button',
  PIN_INPUT = 'verify-email-pin-input',
}
